/**
 * ProviderReferralResponse.tsx
 *
 * Secure public page for providers to accept/decline service referrals.
 * Uses token-based authentication (no login required).
 *
 * URL patterns:
 * - /referral/accept?token={token}
 * - /referral/decline?token={token}
 * - /referral/view?token={token}
 */

import { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Loader2, Clock, MapPin, FileText, Building2, Phone, Mail, ArrowLeft, DollarSign, Send } from 'lucide-react';
import { useFormattedDate } from '../../hooks/useUserTimezone';

// ============================================================================
// PRICING REVIEW SECTION COMPONENT
// ============================================================================

interface PricingInfo {
  referral_id: number;
  referral_number: string;
  pricing_status: string;
  service_type: string;
  cbo_name: string;
  pricing: {
    proposed_rate: number | null;
    proposed_units: number;
    billing_code: string | null;
    billing_unit: string;
    total_amount: number;
  };
  counter_proposal: {
    counter_rate: number | null;
    notes: string | null;
  } | null;
  accepted_rate: number | null;
}

function PricingReviewSection({ token }: { token: string }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pricing, setPricing] = useState<PricingInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<'accepted' | 'counter' | null>(null);
  const [showCounter, setShowCounter] = useState(false);
  const [counterRate, setCounterRate] = useState('');
  const [counterNotes, setCounterNotes] = useState('');

  useEffect(() => {
    loadPricing();
  }, [token]);

  const loadPricing = async () => {
    try {
      const response = await fetch(`/api/service-referrals/token/pricing?token=${encodeURIComponent(token)}`);
      if (!response.ok) {
        // If pricing endpoint doesn't exist yet (column not added), show simple success
        if (response.status === 400 || response.status === 500) {
          setPricing(null);
          return;
        }
        throw new Error('Failed to load pricing');
      }
      const data = await response.json();
      setPricing(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptPricing = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`/api/service-referrals/token/pricing/accept?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error('Failed to accept pricing');
      setSuccess('accepted');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCounterPropose = async () => {
    if (!counterRate) return;
    setSubmitting(true);
    try {
      const response = await fetch(`/api/service-referrals/token/pricing/counter?token=${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          counter_rate: parseFloat(counterRate),
          notes: counterNotes,
        }),
      });
      if (!response.ok) throw new Error('Failed to submit counter-proposal');
      setSuccess('counter');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading pricing details...</span>
      </div>
    );
  }

  if (success === 'accepted') {
    return (
      <div className="text-center py-6">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-green-800">Service Initiated!</h3>
        <p className="text-gray-600 mt-2">
          Pricing has been accepted. The service has been initiated and milestones are now active.
        </p>
      </div>
    );
  }

  if (success === 'counter') {
    return (
      <div className="text-center py-6">
        <Send className="h-12 w-12 text-blue-500 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-blue-800">Counter-Proposal Submitted</h3>
        <p className="text-gray-600 mt-2">
          Your counter-proposal of ${counterRate} has been submitted for review.
          The referring organization will respond shortly.
        </p>
      </div>
    );
  }

  if (!pricing || !pricing.pricing?.proposed_rate) {
    return (
      <div className="text-center py-6">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-green-800">Referral Accepted</h3>
        <p className="text-gray-600 mt-2">
          Your acceptance has been recorded. The referring organization will send
          you the full patient details and pricing information.
        </p>
      </div>
    );
  }

  if (pricing.pricing_status === 'finalized') {
    return (
      <div className="text-center py-6">
        <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-green-800">Pricing Finalized</h3>
        <p className="text-gray-600 mt-2">
          Pricing has been finalized at ${pricing.accepted_rate?.toFixed(2)}.
          The service is now active.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pricing Details */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-green-600" />
          Proposed Pricing
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-500">Rate per {pricing.pricing.billing_unit || 'session'}</p>
            <p className="text-xl font-bold text-gray-900">${pricing.pricing.proposed_rate?.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Estimated Units</p>
            <p className="text-xl font-bold text-gray-900">{pricing.pricing.proposed_units}</p>
          </div>
          <div className="col-span-2 border-t pt-3">
            <p className="text-sm text-gray-500">Estimated Total</p>
            <p className="text-2xl font-bold text-green-600">${pricing.pricing.total_amount.toFixed(2)}</p>
          </div>
        </div>
        {pricing.pricing.billing_code && (
          <p className="text-xs text-gray-500 mt-2">Billing Code: {pricing.pricing.billing_code}</p>
        )}
      </div>

      {/* Counter-proposal section */}
      {showCounter ? (
        <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
          <h4 className="font-medium text-amber-900 mb-3">Submit Counter-Proposal</h4>
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Your Proposed Rate (per {pricing.pricing.billing_unit || 'session'})
              </label>
              <div className="relative">
                <span className="absolute left-3 top-2 text-gray-500">$</span>
                <input
                  type="number"
                  value={counterRate}
                  onChange={(e) => setCounterRate(e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Justification (Optional)
              </label>
              <textarea
                value={counterNotes}
                onChange={(e) => setCounterNotes(e.target.value)}
                placeholder="Explain why you're proposing a different rate..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowCounter(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleCounterPropose}
                disabled={!counterRate || submitting}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {submitting ? 'Submitting...' : 'Submit Counter-Proposal'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex gap-3">
          <button
            onClick={() => setShowCounter(true)}
            className="flex-1 px-4 py-3 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors"
          >
            Counter-Propose
          </button>
          <button
            onClick={handleAcceptPricing}
            disabled={submitting}
            className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle className="h-4 w-4" />
            )}
            Accept & Start Service
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
          {error}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface ReferralDetails {
  valid: boolean;
  referral_id: string;
  referral_number: string;
  referral_status: string;
  provider_id: string;
  provider_response_status: string;
  expires_at: string;
  cbo_name: string;
  member_location: string;
  primary_diagnosis: string;
  sdoh_needs: string[];
  urgency_level: string;
  service_type?: string;
  service_description?: string;
}

export function ProviderReferralResponse() {
  const { fmt } = useFormattedDate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referral, setReferral] = useState<ReferralDetails | null>(null);
  const [action, setAction] = useState<'view' | 'accept' | 'decline'>('view');
  const [declineReason, setDeclineReason] = useState('');
  const [notes, setNotes] = useState('');
  const [success, setSuccess] = useState<'accepted' | 'declined' | null>(null);

  // Parse URL to get token and action
  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (path.includes('/accept')) setAction('accept');
    else if (path.includes('/decline')) setAction('decline');
    else setAction('view');

    if (!token) {
      setError('Invalid or missing referral token. Please use the link from your email.');
      setLoading(false);
      return;
    }

    // Verify token and get referral details
    verifyToken(token);
  }, []);

  const verifyToken = async (token: string) => {
    try {
      // Try service referrals API first (for employer platform referrals)
      let response = await fetch(`/api/service-referrals/token/verify?token=${encodeURIComponent(token)}`);

      // Fall back to voice-workflows API for ECM referrals
      if (!response.ok) {
        response = await fetch(`/api/voice-workflows/referral-token/verify?token=${encodeURIComponent(token)}`);
      }

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Token verification failed');
      }

      const data = await response.json();
      setReferral(data);

      // Check if already responded
      if (data.provider_response_status !== 'pending') {
        setSuccess(data.provider_response_status as 'accepted' | 'declined');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify referral. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    if (!referral) return;
    setSubmitting(true);

    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');

      // Try service referrals API first
      let response = await fetch(`/api/service-referrals/token/accept?token=${encodeURIComponent(token!)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      });

      // Fall back to voice-workflows API for ECM referrals
      if (!response.ok && response.status === 404) {
        response = await fetch(`/api/voice-workflows/referral-token/accept?token=${encodeURIComponent(token!)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ notes }),
        });
      }

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to accept referral');
      }

      setSuccess('accepted');
    } catch (err: any) {
      setError(err.message || 'Failed to accept referral');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (!referral || !declineReason) return;
    setSubmitting(true);

    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');

      // Try service referrals API first
      let response = await fetch(`/api/service-referrals/token/decline?token=${encodeURIComponent(token!)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: declineReason, notes }),
      });

      // Fall back to voice-workflows API for ECM referrals
      if (!response.ok && response.status === 404) {
        response = await fetch(`/api/voice-workflows/referral-token/reject?token=${encodeURIComponent(token!)}&reason=${encodeURIComponent(declineReason)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ notes }),
        });
      }

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to decline referral');
      }

      setSuccess('declined');
    } catch (err: any) {
      setError(err.message || 'Failed to decline referral');
    } finally {
      setSubmitting(false);
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency?.toLowerCase()) {
      case 'urgent':
      case 'high':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Verifying referral...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-red-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Access Referral</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <p className="text-sm text-gray-500">
            If you believe this is an error, please contact the referring organization.
          </p>
        </div>
      </div>
    );
  }

  // Success state - show pricing step after acceptance
  if (success === 'accepted') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-8 px-4">
        <div className="max-w-2xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-6">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Referral Accepted!</h1>
            <p className="text-gray-600">
              Thank you for accepting referral #{referral?.referral_number}.
              PHI has been released and will be sent to you.
            </p>
          </div>

          {/* Pricing Review Card */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
            <div className="bg-blue-600 text-white px-6 py-4">
              <h2 className="text-lg font-semibold">Step 2: Review & Confirm Pricing</h2>
              <p className="text-sm opacity-80">Please review and accept the pricing to begin service delivery</p>
            </div>

            <div className="p-6">
              <PricingReviewSection token={new URLSearchParams(window.location.search).get('token') || ''} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Declined state
  if (success === 'declined') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
          <XCircle className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Referral Declined</h1>
          <p className="text-gray-600 mb-4">
            This referral has been declined. The referring organization has been notified.
          </p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-left">
            <p className="text-sm text-gray-700">
              <strong>Referral #{referral?.referral_number}</strong>
              <br />
              They may reach out to discuss or offer alternative referrals.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main referral view
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <FileText className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Service Referral Request</h1>
          <p className="text-gray-600">
            From <strong>{referral?.cbo_name || 'WeaveIn'}</strong>
          </p>
        </div>

        {/* Referral Details Card */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          {/* Referral Number Banner */}
          <div className="bg-blue-600 text-white px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">Referral Number</p>
                <p className="text-xl font-bold">{referral?.referral_number}</p>
              </div>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${getUrgencyColor(referral?.urgency_level || '')}`}>
                {referral?.urgency_level || 'Routine'}
              </span>
            </div>
          </div>

          {/* Details */}
          <div className="p-6 space-y-4">
            {referral?.primary_diagnosis && (
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Service Type</p>
                  <p className="font-medium text-gray-900">{referral.primary_diagnosis}</p>
                </div>
              </div>
            )}

            {referral?.member_location && (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Member Location</p>
                  <p className="font-medium text-gray-900">{referral.member_location}</p>
                </div>
              </div>
            )}

            {referral?.sdoh_needs && referral.sdoh_needs.length > 0 && (
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">SDOH Needs</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {referral.sdoh_needs.map((need, i) => (
                      <span key={i} className="px-2 py-1 bg-purple-50 text-purple-700 text-sm rounded-md">
                        {need}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {referral?.expires_at && (
              <div className="flex items-start gap-3">
                <Clock className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Response Deadline</p>
                  <p className="font-medium text-gray-900">
                    {fmt.dateTime(referral.expires_at)}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* PHI Notice */}
          <div className="bg-amber-50 border-t border-amber-100 px-6 py-4">
            <p className="text-sm text-amber-800">
              <strong>Protected Health Information:</strong> Full patient details will be shared after you accept this referral.
            </p>
          </div>
        </div>

        {/* Action Buttons or Decline Form */}
        {action === 'decline' ? (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Decline Referral</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Declining <span className="text-red-500">*</span>
                </label>
                <select
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select a reason...</option>
                  <option value="no_capacity">No current capacity</option>
                  <option value="outside_area">Outside service area</option>
                  <option value="outside_scope">Services outside scope of practice</option>
                  <option value="scheduling_conflict">Scheduling conflict</option>
                  <option value="other">Other reason</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Any additional details..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setAction('view')}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </button>
                <button
                  onClick={handleDecline}
                  disabled={!declineReason || submitting}
                  className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  Confirm Decline
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Respond to Referral</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Any notes about your response..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setAction('decline')}
                  disabled={submitting}
                  className="flex-1 px-4 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  <XCircle className="h-4 w-4" />
                  Decline
                </button>
                <button
                  onClick={handleAccept}
                  disabled={submitting}
                  className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4" />
                  )}
                  Accept Referral
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Contact Info */}
        <div className="mt-8 text-center text-sm text-gray-500">
          <p>Questions? Contact the referring organization:</p>
          <p className="font-medium text-gray-700">{referral?.cbo_name || 'WeaveIn'}</p>
        </div>
      </div>
    </div>
  );
}

export default ProviderReferralResponse;
